import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../redux";
import { useSelector } from "react-redux";
import {
  getReportFileName,
  getATSFarmaciaOption,
  getCityOption,
  getDistrictOption,
  getRegionalCodesOption,
  getSurveyCampaignOption,
  getSurveyOption,
  getSurveySlugOption,
  updateFilterParams,
} from "../../redux/reducer/surveyReportReducer";
import moment from "moment";
import SelectDropdown from "../common/SelectDropdown";
import DatePickerInput from "../common/datePickerInput";
import ButtonWhite from "../common/buttonWhite";
import ButtonSubmit from "../common/buttonSubmit";
import DropDownButton from "../common/dropDownButton";
import { downloadDocument, getFormattedDate } from "../../utils/helpers";

// Component starts
const SurveyFilterForm = (props: any) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();


  /**
   * redux hooks.
   */
  const { eventId, user } = useSelector((state: any) => state.login);

  let downloadOptions = [
    { key: "aggregate", label: t("aggregate-for-pharmacy") },
    { key: "pseudo-anonymous", label: t("report-psedoanonimo") }
  ];

  if (user.modules && user.modules.includes("FEDERFARMA_REPORT_CF_DOWNLOAD")) {
    downloadOptions.push({ key: "without-pseudo-anonymization", label: t("report-with-fiscal-code") })
  }

  /**
   * Get props.
   */
  const { isAdminUser, region } = props;

  /**
   * search fields state variables.
   */
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [campaign, setCampaign] = useState("");
  const [surveySlug, setSurveySlug] = useState("");
  const [surveyId, setSurveyId] = useState("");
  const [regionalCode, setRegionalCode] = useState("");
  const [atsFarmacia, setAtsFarmacia] = useState("");
  const [prescriptionStartDate, setPrescriptionStartDate] = useState(null);
  const [prescriptionEndDate, setPrescriptionEndDate] = useState(null);
  const [compilationStartDate, setCompilationStartDate] = useState(null);
  const [compilationEndDate, setCompilationEndDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
   * set options in state variables.
   */
  const [districtOption, setDistrictOption] = useState([]);
  const [cityOption, setCityOption] = useState([]);
  const [campaignOption, setCampaignOption] = useState([]);
  const [surveyOption, setSurveyOption] = useState([]);
  const [surveySlugOption, setSurveySlugOption] = useState([]);
  const [regionalCodeOption, setRegionalCodeOption] = useState([]);
  const [atsFarmaciaoption, setAtsFarmaciaoption] = useState([]);
  const [spinner, setSpinner] = useState(false);

  /**
   * fetch dropdown options.
   */
  useEffect(() => {
    fetchData();
    handleReset();
  }, []);

  const fetchData = async () => {
    const campaigns = await dispatch(getSurveyCampaignOption());
    setCampaignOption(campaigns);

    if (eventId) {
      const survey = await dispatch(getSurveyOption());
      setSurveyOption(survey);
    }

    if (isAdminUser) {
      // dispatch methods to fetch dropdown options
      const district = await dispatch(getDistrictOption());
      const cities = await dispatch(getCityOption());
      const surveySlugs = await dispatch(getSurveySlugOption());
      const regionalCodes = await dispatch(getRegionalCodesOption());
      const atsFarmacia = await dispatch(getATSFarmaciaOption());
      // set options
      setDistrictOption(district);
      setCityOption(cities);
      setSurveySlugOption(surveySlugs);
      setRegionalCodeOption(regionalCodes);
      setAtsFarmaciaoption(atsFarmacia);
    }
  };

  /**
   * reset form.
   */
  const handleReset = () => {
    setDistrict("");
    setCity("");
    setCampaign("");
    setSurveySlug("");
    setSurveyId("");
    setRegionalCode("");
    setAtsFarmacia("");
    setPrescriptionStartDate(null);
    setPrescriptionEndDate(null);
    setCompilationStartDate(null);
    setCompilationEndDate(null);
    setStartDate(null);
    setEndDate(null);
    dispatch(updateFilterParams({}, isAdminUser));
  };

  /**
   * Get admin filter params.
   */
  const getAdminFilterParams = () => {
    return {
      district,
      city,
      regionalCode,
      atsCode: atsFarmacia,
      campaign,
      surveySlug,
      prescriptionStartDate: getFormattedDate(prescriptionStartDate),
      prescriptionEndDate: getFormattedDate(prescriptionEndDate),
      compilationStartDate: getFormattedDate(compilationStartDate),
      compilationEndDate: getFormattedDate(compilationEndDate),
      startDate: getFormattedDate(startDate),
      endDate: getFormattedDate(endDate),
    };
  };

  /**
   * Get filter params.
   */
  const getFilterParams = (field: string = "") => {
    const surveyField = field ? field : "survey";
    return {
      campaign,
      [surveyField]: surveyId,
      prescriptionStartDate: getFormattedDate(prescriptionStartDate),
      prescriptionEndDate: getFormattedDate(prescriptionEndDate),
      compilationStartDate: getFormattedDate(compilationStartDate),
      compilationEndDate: getFormattedDate(compilationEndDate),
      startDate: getFormattedDate(startDate),
      endDate: getFormattedDate(endDate),
    };
  };

  /**
   * filter survey.
   */
  const handleFilter = async () => {
    setSpinner(true);
    try {
      const params = isAdminUser ? getAdminFilterParams() : getFilterParams();
      await dispatch(updateFilterParams(params, isAdminUser));
    } catch (e) {
      console.log("Filter survey report:", e);
    } finally {
      setTimeout(() => setSpinner(false), 500);
    }
  };

  /**
   * handle document exports.
   */
  const handleExport = async (type: string) => {
    setLoading(true);
    try {
      let params = isAdminUser
        ? getAdminFilterParams()
        : getFilterParams("surveySlug");
      if (region) params = { ...params, regionalCode: region };
      const fileName = await dispatch(getReportFileName(type, params));
      if (fileName) {
        downloadDocument(
          `${process.env.REACT_APP_API}/t4event/api/v01/event/file/${fileName}`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * selected date.
   */
  const getSelectedDate = (date: any) => {
    return date ? moment(date, "DD-MM-YYYY").toDate() : null;
  };

  return (
    <div className="container-box mb-3">
      <div className="survey-filters">
        <div className="survey-filters-field col-md-12">
          {/* filter by province */}
          {isAdminUser && (
            <div className="filter-input">
              <label>{t("province")}</label>
              <SelectDropdown
                value={district}
                options={districtOption}
                handleChange={setDistrict}
              />
            </div>
          )}

          {/* filter by city */}
          {isAdminUser && (
            <div className="filter-input">
              <label>{t("city")}</label>
              <SelectDropdown
                value={city}
                options={cityOption}
                handleChange={setCity}
              />
            </div>
          )}

          {/* filter by campagna */}
          <div className="filter-input">
            <label>{t("campaign")}</label>
            <SelectDropdown
              value={campaign}
              options={campaignOption}
              handleChange={setCampaign}
            />
          </div>

          {/* filter by survey slug */}
          {isAdminUser && (
            <div className="filter-input">
              <label>{t("survey-slug")}</label>
              <SelectDropdown
                value={surveySlug}
                options={surveySlugOption}
                handleChange={setSurveySlug}
              />
            </div>
          )}

          {/* filter by survey option */}
          {!isAdminUser && (
            <div className="filter-input">
              <label>{t("survey-slug")}</label>
              <SelectDropdown
                value={surveyId}
                options={surveyOption.map((option: any) => ({
                  key: option.slug,
                  label: option.name,
                }))}
                handleChange={setSurveyId}
              />
            </div>
          )}

          {/* filter by Regional code */}
          {isAdminUser && (
            <div className="filter-input">
              <label>{t("regional-code")}</label>
              <SelectDropdown
                value={regionalCode}
                options={regionalCodeOption}
                handleChange={setRegionalCode}
              />
            </div>
          )}

          {/* filter by ats farmacia */}
          {isAdminUser && (
            <div className="filter-input">
              <label>{t("ats-pharmacy")}</label>
              <SelectDropdown
                value={atsFarmacia}
                options={atsFarmaciaoption}
                handleChange={setAtsFarmacia}
              />
            </div>
          )}

          {/* filter by data_prescrizione */}
          <div className="filter-input">
            <label>{t("prescription-date")}</label>
            <div className="datepicker">
              <DatePickerInput
                maxDate={getSelectedDate(prescriptionEndDate)}
                disabled={false}
                className="w-100 mt-0 ms-0 mb-2 d-flex align-item-center justify-content-center input"
                handleChange={setPrescriptionStartDate}
                firstSelect="DD/MM/YYYY"
                dateSelected={
                  prescriptionStartDate
                    ? moment(prescriptionStartDate, "DD-MM-YYYY").toDate()
                    : null
                }
              />
            </div>
          </div>

          {/* filter by prescription-end-date */}
          <div className="filter-input">
            <label>{t("prescription-end-date")}</label>
            <div className="datepicker">
              <DatePickerInput
                minDate={getSelectedDate(prescriptionStartDate)}
                disabled={false}
                className="w-100 mt-0 ms-0 mb-2 d-flex align-item-center justify-content-center input"
                handleChange={setPrescriptionEndDate}
                firstSelect="DD/MM/YYYY"
                dateSelected={
                  prescriptionEndDate
                    ? moment(prescriptionEndDate, "DD-MM-YYYY").toDate()
                    : null
                }
              />
            </div>
          </div>

          {/* filter by compilationStartDate */}
          <div className="filter-input">
            <label>{t("compilation-start-date")}</label>
            <div className="datepicker">
              <DatePickerInput
                maxDate={getSelectedDate(compilationEndDate)}
                disabled={false}
                className="w-100 mt-0 ms-0 mb-2 d-flex align-item-center justify-content-center input"
                handleChange={setCompilationStartDate}
                firstSelect="DD/MM/YYYY"
                dateSelected={
                  compilationStartDate
                    ? moment(compilationStartDate, "DD-MM-YYYY").toDate()
                    : null
                }
              />
            </div>
          </div>

          {/* filter by compilationEndDate */}
          <div className="filter-input">
            <label>{t("compilation-end-date")}</label>
            <div className="datepicker">
              <DatePickerInput
                minDate={getSelectedDate(compilationStartDate)}
                disabled={false}
                className="w-100 mt-0 ms-0 mb-2 d-flex align-item-center justify-content-center input"
                handleChange={setCompilationEndDate}
                firstSelect="DD/MM/YYYY"
                dateSelected={
                  compilationEndDate
                    ? moment(compilationEndDate, "DD-MM-YYYY").toDate()
                    : null
                }
              />
            </div>
          </div>

          {/* filter by startDate */}
          <div className="filter-input">
            <label>{t("start-date")}</label>
            <div className="datepicker">
              <DatePickerInput
                maxDate={getSelectedDate(endDate)}
                disabled={false}
                className="w-100 mt-0 ms-0 mb-2 d-flex align-item-center justify-content-center input"
                handleChange={setStartDate}
                firstSelect="DD/MM/YYYY"
                dateSelected={
                  startDate ? moment(startDate, "DD-MM-YYYY").toDate() : null
                }
              />
            </div>
          </div>
          {/* filter by endDate */}
          <div className="filter-input">
            <label>{t("end-date")}</label>
            <div className="datepicker">
              <DatePickerInput
                minDate={getSelectedDate(startDate)}
                disabled={false}
                className="w-100 mt-0 ms-0 mb-2 d-flex align-item-center justify-content-center input"
                handleChange={setEndDate}
                firstSelect="DD/MM/YYYY"
                dateSelected={
                  endDate ? moment(endDate, "DD-MM-YYYY").toDate() : null
                }
              />
            </div>
          </div>
        </div>
        <div className="filters-btn">
          <ButtonWhite
            textButton={t("reset-search")}
            handleChange={handleReset}
          />
          <ButtonSubmit
            spinner={spinner}
            textButton={t("search")}
            handleChange={handleFilter}
            className="m-1"
          />
          <DropDownButton
            loading={loading}
            textButton={t("download-selection")}
            handleClick={handleExport}
            dropdownItems={downloadOptions}
            className="p-1"
          />
        </div>
      </div>
    </div>
  );
};

export default SurveyFilterForm;
